import {
  getLeoUuid,
  LeoUUID,
  getList,
  getObject,
} from "@surya-digital/leo-ts-types";
import { SurveyRuleCondition, SurveyRuleConditionDTO, SurveyRuleConditionEnums } from "./surveyRuleCondition";
import { SurveyRuleAction } from "./surveyRuleAction";
import { SurveyRuleTarget, SurveyRuleTargetDTO } from "./surveyRuleTarget";

export interface SurveyRuleDTO {
  ruleId: string;
  conditions: SurveyRuleConditionDTO[];
  action: SurveyRuleAction.SurveyRuleActionDTO;
  target: SurveyRuleTargetDTO[];
}

export class SurveyRule {
  readonly isSurveyRuleSurveyRule: boolean = true;

  readonly ruleId: LeoUUID;
  readonly conditions: SurveyRuleCondition[];
  readonly action: SurveyRuleAction.SurveyRuleAction;
  readonly target: SurveyRuleTarget[];

  static readonly constraints = {
    "conditions": {
      "minSize": 1,
    },
    "target": {
      "minSize": 1,
    }
  }

  constructor(
  ruleId: LeoUUID, 
  conditions: SurveyRuleCondition[], 
  action: SurveyRuleAction.SurveyRuleAction, 
  target: SurveyRuleTarget[]
  ) {
    this.ruleId = ruleId;
    SurveyRule.validateConditions(conditions);
    this.conditions = conditions;
    this.action = action;
    SurveyRule.validateTarget(target);
    this.target = target;
  }

  static fromDTO(dto: SurveyRuleDTO): SurveyRule {
    const ruleId = getLeoUuid(dto, "ruleId", InvalidSurveyRuleRuleIdError);
    const conditions = getList<SurveyRuleConditionDTO, SurveyRuleCondition>(
      dto,
      "conditions",
      SurveyRuleCondition.fromDTO,
      InvalidSurveyRuleConditionsError
    );
    const action = SurveyRuleAction.fromDTO(getObject(dto, "action", InvalidSurveyRuleActionError));
    const target = getList<SurveyRuleTargetDTO, SurveyRuleTarget>(
      dto,
      "target",
      SurveyRuleTarget.fromDTO,
      InvalidSurveyRuleTargetError
    );
    return new SurveyRule(
      ruleId,
      conditions,
      action,
      target
    );
  }

  toDTO(): SurveyRuleDTO {
    const dto: SurveyRuleDTO = {
        ruleId: this.ruleId.toDTO(),
        conditions: this.conditions.map((e: SurveyRuleCondition) => { return e.toDTO() }),
        action: SurveyRuleAction.toDTO(this.action),
        target: this.target.map((e: SurveyRuleTarget) => { return e.toDTO() })
    };
    return dto;
  }
  private static validateConditions = (conditions: SurveyRuleCondition[]): void => {
    if(conditions.length < 1) {
      throw new InvalidSurveyRuleConditionsMinSizeError(
        1,
        `Attribute conditions is too short. Size is ${conditions.length}. Min size is 1.`
      );
    }
  }
  private static validateTarget = (target: SurveyRuleTarget[]): void => {
    if(target.length < 1) {
      throw new InvalidSurveyRuleTargetMinSizeError(
        1,
        `Attribute target is too short. Size is ${target.length}. Min size is 1.`
      );
    }
  }
  
}

SurveyRule.prototype.toString = function toString(): string {
  return `SurveyRule(ruleId='${this.ruleId}',conditions='${this.conditions}',action='${this.action}',target='${this.target}')`;
};

export class InvalidSurveyRuleError extends Error {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSurveyRuleError";
  }
}
export class InvalidSurveyRuleRuleIdError extends InvalidSurveyRuleError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSurveyRuleRuleIdError";
  }
}

export class InvalidSurveyRuleConditionsError extends InvalidSurveyRuleError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSurveyRuleConditionsError";
  }
}

export class InvalidSurveyRuleConditionsMinSizeError extends InvalidSurveyRuleConditionsError {
  readonly minSize: number;
  constructor(minSize: number, message?: string) {
    super(message);
    this.minSize = minSize;
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSurveyRuleConditionsMinSizeError";
  }
}

export class InvalidSurveyRuleActionError extends InvalidSurveyRuleError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSurveyRuleActionError";
  }
}

export class InvalidSurveyRuleTargetError extends InvalidSurveyRuleError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSurveyRuleTargetError";
  }
}

export class InvalidSurveyRuleTargetMinSizeError extends InvalidSurveyRuleTargetError {
  readonly minSize: number;
  constructor(minSize: number, message?: string) {
    super(message);
    this.minSize = minSize;
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSurveyRuleTargetMinSizeError";
  }
}
